import React, {useMemo, useState, useEffect, useCallback} from 'react'
import {StyleSheet, View} from 'react-native'
import {type AppBskyFeedDefs} from '@atproto/api'
import {msg} from '@lingui/macro'
import {useLingui} from '@lingui/react'

import {cleanError} from '#/lib/strings/errors'
import {useFeedsQuery, useFeedVideosQuery} from '#/state/queries/word-groups'
import {usePostShadow, POST_TOMBSTONE} from '#/state/cache/post-shadow'
import {ListMaybePlaceholder} from '#/components/Lists'
import {ThreeColumnPostThread} from './ThreeColumnPostThread'
import {FeedSelector} from './WordGroupSelector'

export function ThreeVideoFeed({testID}: {testID?: string}) {
  const {_} = useLingui()

  // Get available feeds to determine default
  const {data: feeds} = useFeedsQuery()
  const defaultFeedSlug = feeds?.[0]?.slug ?? null

  // Feed selection state - default to first available feed
  const [selectedFeedSlug, setSelectedFeedSlug] = useState<string | null>(null)

  // Use selected feed or default to first feed
  const activeFeedSlug = selectedFeedSlug ?? defaultFeedSlug

  // Feed videos query
  const {
    data,
    isFetched,
    isLoading,
    isError,
    error,
    refetch,
    fetchNextPage,
    hasNextPage,
  } = useFeedVideosQuery(activeFeedSlug)

  // Flatten all posts from pages
  const allPosts = useMemo(() => {
    if (!data?.pages) return []
    return data.pages.flatMap(page => page.posts) || []
  }, [data])

  // Track the current index in the queue for each of the 3 video slots
  const [videoIndices, setVideoIndices] = useState([0, 1, 2])

  // Reset video indices when feed changes
  const handleFeedChange = useCallback((slug: string | null) => {
    setSelectedFeedSlug(slug)
    setVideoIndices([0, 1, 2])
  }, [])

  // Get the current 3 videos to display
  const currentVideos = useMemo(() => {
    return videoIndices.map(idx => allPosts[idx]).filter(Boolean)
  }, [allPosts, videoIndices])

  // Prefetch more videos when we're getting close to the end
  useEffect(() => {
    const maxIndex = Math.max(...videoIndices)
    const bufferSize = 6 // Keep 6 videos ahead buffered

    // If we don't have enough posts buffered and there's more to fetch
    if (allPosts.length < maxIndex + bufferSize && hasNextPage) {
      fetchNextPage()
    }
  }, [videoIndices, allPosts.length, hasNextPage, fetchNextPage])

  const handleVideoEnd = (slotIndex: number) => {
    setVideoIndices(prev => {
      const newIndices = [...prev]
      // Find the next available video index after all current videos
      const maxIndex = Math.max(...prev)
      newIndices[slotIndex] = maxIndex + 1
      return newIndices
    })
  }

  if (isLoading || !isFetched) {
    return (
      <View style={styles.container}>
        <FeedSelector
          selectedFeedSlug={activeFeedSlug}
          onSelectFeed={handleFeedChange}
        />
        <ListMaybePlaceholder
          isLoading={true}
          isError={false}
          onRetry={refetch}
          emptyType="results"
          emptyMessage=""
          errorMessage=""
        />
      </View>
    )
  }

  if (isError) {
    return (
      <View style={styles.container}>
        <FeedSelector
          selectedFeedSlug={activeFeedSlug}
          onSelectFeed={handleFeedChange}
        />
        <ListMaybePlaceholder
          isLoading={false}
          isError={true}
          onRetry={refetch}
          emptyType="results"
          emptyMessage=""
          errorMessage={cleanError(error)}
        />
      </View>
    )
  }

  if (currentVideos.length === 0) {
    return (
      <View style={styles.container}>
        <FeedSelector
          selectedFeedSlug={activeFeedSlug}
          onSelectFeed={handleFeedChange}
        />
        <ListMaybePlaceholder
          isLoading={false}
          isError={false}
          onRetry={refetch}
          emptyType="results"
          emptyMessage={_(msg`No videos found in this feed.`)}
          errorMessage=""
        />
      </View>
    )
  }

  return (
    <View testID={testID} style={styles.container}>
      <FeedSelector
        selectedFeedSlug={activeFeedSlug}
        onSelectFeed={handleFeedChange}
      />
      <View style={styles.videoGrid}>
        {currentVideos.map((post, slotIndex) => (
          <VideoWithModeration
            key={`${post.uri}-${videoIndices[slotIndex]}`}
            post={post}
            onVideoEnd={() => handleVideoEnd(slotIndex)}
          />
        ))}
      </View>
    </View>
  )
}

function VideoWithModeration({
  post,
  onVideoEnd,
}: {
  post: AppBskyFeedDefs.PostView
  onVideoEnd: () => void
}) {
  const postShadowed = usePostShadow(post)

  if (postShadowed === POST_TOMBSTONE) {
    return null
  }

  return (
    <ThreeColumnPostThread
      uri={postShadowed.uri}
      onVideoEnd={onVideoEnd}
    />
  )
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#000',
    marginLeft: 80,
  },
  videoGrid: {
    flexDirection: 'row',
    justifyContent: 'flex-start',
    alignItems: 'flex-start',
    gap: 2,
  },
})
